import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { PRODUCTS } from "../data/content";

const STORAGE_KEY = "yaba_bag";
const CartContext = createContext(null);

const priceEur = (str) => parseInt(String(str).replace(/\D/g, ""), 10) || 0;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // migrate any legacy entries missing an id
      return parsed
        .filter((i) => i && i.slug)
        .map((i) => ({
          id: i.id || `${i.slug}-${i.size || "M"}-${i.ts || Date.now()}`,
          slug: i.slug,
          size: i.size || "M",
          qty: Math.max(1, Math.min(9, i.qty || 1)),
          gift: i.gift || null,
          ts: i.ts || Date.now(),
        }));
    } catch {
      return [];
    }
  });
  const [promoCode, setPromoCode] = useState(() => {
    try { return window.localStorage.getItem("yaba_promo") || ""; } catch { return ""; }
  });

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch {}
  }, [items]);
  useEffect(() => {
    try {
      if (promoCode) window.localStorage.setItem("yaba_promo", promoCode);
      else window.localStorage.removeItem("yaba_promo");
    } catch {}
  }, [promoCode]);

  const addItem = useCallback((entry) => {
    setItems((cur) => {
      // merge same slug+size (unless gift note differs)
      const idx = cur.findIndex(
        (i) => i.slug === entry.slug && i.size === entry.size && (i.gift || null) === (entry.gift || null)
      );
      if (idx >= 0) {
        const next = cur.slice();
        next[idx] = { ...next[idx], qty: Math.min(9, next[idx].qty + (entry.qty || 1)) };
        return next;
      }
      return [
        ...cur,
        {
          id: `${entry.slug}-${entry.size}-${Date.now()}`,
          slug: entry.slug,
          size: entry.size || "M",
          qty: entry.qty || 1,
          gift: entry.gift || null,
          ts: Date.now(),
        },
      ];
    });
  }, []);

  const updateQty = useCallback((id, qty) => {
    setItems((cur) =>
      cur.map((i) => (i.id === id ? { ...i, qty: Math.max(1, Math.min(9, qty)) } : i))
    );
  }, []);

  const updateGift = useCallback((id, gift) => {
    setItems((cur) => cur.map((i) => (i.id === id ? { ...i, gift: gift || null } : i)));
  }, []);

  const removeItem = useCallback((id) => {
    setItems((cur) => cur.filter((i) => i.id !== id));
  }, []);

  const clear = useCallback(() => {
    setItems([]);
    setPromoCode("");
  }, []);

  const enriched = useMemo(
    () =>
      items
        .map((i) => {
          const p = PRODUCTS.find((x) => x.slug === i.slug);
          if (!p) return null;
          return { ...i, product: p, lineTotal: priceEur(p.price) * i.qty };
        })
        .filter(Boolean),
    [items]
  );

  const count = useMemo(() => items.reduce((a, i) => a + i.qty, 0), [items]);
  const subtotal = useMemo(
    () => enriched.reduce((a, i) => a + i.lineTotal, 0),
    [enriched]
  );
  const discount = useMemo(
    () => (promoCode.trim().toUpperCase() === "YABA10" ? Math.round(subtotal * 0.1) : 0),
    [promoCode, subtotal]
  );
  const shippingThreshold = 200;
  // Base shipping on subtotal (pre-discount) so applying the promo can't flip
  // a customer back to paid shipping — a promo should never punish you.
  const shipping = subtotal >= shippingThreshold || subtotal === 0 ? 0 : 12;
  const total = Math.max(0, subtotal - discount + shipping);

  const value = {
    items: enriched, rawItems: items,
    addItem, updateQty, updateGift, removeItem, clear,
    promoCode, setPromoCode,
    count, subtotal, discount, shipping, total,
    shippingThreshold,
    freeShippingRemaining: Math.max(0, shippingThreshold - subtotal),
  };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
